import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Stethoscope, Users, Calendar, Shield, ArrowRight, Download, Smartphone, Share2, PlusSquare } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroidDevice = /android/i.test(userAgent);
    const isMobileDevice = isIOSDevice || isAndroidDevice || window.innerWidth < 768;
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsMobile(isMobileDevice);

    // Listen for install prompt (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    // If we have the native prompt, use it
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
      return;
    }
    
    // Otherwise show manual instructions
    setShowInstructions(true);
  };

  if (user) {
    if (user.role === 'doctor') navigate('/doctor');
    if (user.role === 'mr') navigate('/mr');
    if (user.role === 'admin') navigate('/admin');
  }

  const features = [
    {
      icon: Stethoscope,
      title: 'For Doctors',
      description: 'Manage your schedule, view appointments, and approve bookings effortlessly.',
    },
    {
      icon: Users,
      title: 'For Medical Reps',
      description: 'Search doctors, book appointments, and track your meeting history.',
    },
    {
      icon: Shield,
      title: 'For Admins',
      description: 'Monitor platform activity, manage users, and view comprehensive statistics.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-subtle to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-border z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-primary rounded-full flex items-center justify-center">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-heading font-bold text-primary tracking-tight">MRcall</span>
          </div>
          <Button
            data-testid="header-signin-btn"
            onClick={() => navigate('/auth')}
            className="rounded-full px-6 bg-primary hover:bg-primary-hover text-white font-semibold"
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Content - Tetris Grid Style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8 space-y-8"
          >
            <div className="space-y-6">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold text-background-dark leading-none tracking-tight">
                Streamline Doctor-MR Appointments
              </h1>
              <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed max-w-2xl">
                The professional platform for medical representatives to book appointments with doctors seamlessly.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="hero-get-started-btn"
                onClick={() => navigate('/auth')}
                size="lg"
                className="rounded-full px-8 py-6 bg-primary hover:bg-primary-hover text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 text-lg"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              {/* Install App Button - Always visible on mobile */}
              {!isInstalled && (
                <Button
                  data-testid="hero-install-btn"
                  onClick={handleInstallClick}
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 border-2 border-primary text-primary hover:bg-primary hover:text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 text-lg"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Install App
                </Button>
              )}
            </div>
            
            {/* Installation Instructions Modal */}
            {showInstructions && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-5 bg-gradient-to-r from-blue-50 to-teal-50 border border-blue-200 rounded-2xl shadow-lg"
              >
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
                
                {isIOS ? (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Share2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg">Install on iPhone</h4>
                      <ol className="text-sm text-slate-700 mt-3 space-y-2">
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                          Tap the <Share2 className="inline h-4 w-4 mx-1" /> <strong>Share</strong> button at bottom of Safari
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                          Scroll down and tap <strong>"Add to Home Screen"</strong>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                          Tap <strong>"Add"</strong> in top right corner
                        </li>
                      </ol>
                    </div>
                  </div>
                ) : isAndroid ? (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Smartphone className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg">Install on Android</h4>
                      <ol className="text-sm text-slate-700 mt-3 space-y-2">
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                          Tap the <strong>⋮ menu</strong> (3 dots) in top-right of Chrome
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                          Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                          Tap <strong>"Install"</strong> to confirm
                        </li>
                      </ol>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Download className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg">Install App</h4>
                      <p className="text-sm text-slate-700 mt-2">
                        Look for the <strong>install icon</strong> in your browser's address bar, or click the menu and select <strong>"Install MRcall"</strong>
                      </p>
                    </div>
                  </div>
                )}
                
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="mt-4 text-primary text-sm font-medium hover:underline"
                >
                  Got it, close
                </button>
              </motion.div>
            )}
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-4"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-100">
              <img
                src="https://images.unsplash.com/photo-1659353888906-adb3e0041693?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBkb2N0b3IlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGNsaW5pY3xlbnwwfHx8fDE3Njc5ODM5NzZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional Indian doctor"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-4">FEATURES</p>
            <h2 className="text-4xl sm:text-5xl font-heading font-bold text-background-dark tracking-tight">
              Built for Healthcare Professionals
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-8 hover:shadow-md transition-all group"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="h-14 w-14 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-3 text-background-dark">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Join healthcare professionals using MRcall today.
          </p>
          <Button
            data-testid="cta-signup-btn"
            onClick={() => navigate('/auth')}
            size="lg"
            className="rounded-full px-8 py-6 bg-white hover:bg-slate-50 text-primary font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 text-lg"
          >
            Sign Up Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background-dark py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-white/70 text-sm">
          <p>&copy; 2026 MRcall. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="/policy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/policy" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="/policy" className="hover:text-white transition-colors">Cancellation & Refunds</a>
          </div>
        </div>
      </footer>
    </div>
  );
}